import { GroupviewCacheItem, GroupviewModel } from "@/Models/Groupview";
import { TreeRenderElement } from "@/components/ObjectViews/TreeviewRenderer";
import { SectionPlane, SectionPlanesPlugin } from "@xeokit/xeokit-sdk";
import { TreeViewCacheItem, TreeviewModel } from "../../Models/Treeview";
import {
    ActionType,
    AddHighlightedEntities,
    AddInvisibleEntities,
    AddSelectedEntities,
    AddXrayedEntities,
    RemoveHighlightedEntities,
    RemoveInvisibleEntities,
    RemoveSelectedEntity,
    RemoveXrayedEntities,
    ScheduleIsolatedMeasure,
    SetHighlightedEntities,
    SetInvisibleEntities,
    SetOpenPlugin,
    SetPickerIsEnabled,
    SetProjection,
    SetSectionPlanePlugin,
    SetSectionPlanes,
    SetSelectedEntities,
    SetXrayedEntities,
    StopIsolatedMeasure,
    ViewerActions,
} from "./Actions";
import { ViewerState } from "./ViewerState";

const actionCallback = (type: ActionType) => {
    if (window.chrome?.webview?.postMessage) {
        switch (type) {
            case ActionType.AddSelectedEntities:
            case ActionType.RemoveSelectedEntity:
            case ActionType.SetSelectedEntities: {
                window.chrome.webview.postMessage("entitySelectionChanged");
                break;
            }
            case ActionType.AddHighlightedEntities:
            case ActionType.RemoveHighlightedEntities:
            case ActionType.SetHighlightedEntities: {
                window.chrome.webview.postMessage("entityHighlightChanged");
                break;
            }
            case ActionType.AddInvisibleEntities:
            case ActionType.RemoveInvisibleEntities:
            case ActionType.SetInvisibleEntities: {
                window.chrome.webview.postMessage("entityVisibilityChanged");
                break;
            }
        }
    }
};

const viewerReducer = (state: ViewerState, action: ViewerActions): ViewerState => {
    actionCallback(action.type);
    switch (action.type) {
        case ActionType.AddSelectedEntities: {
            //remove duplicates
            let selectedEntities = [...state.selectedEntities, ...action.entities];
            selectedEntities = selectedEntities.filter((e, i) => selectedEntities.indexOf(e) === i);
            return {
                ...state,
                selectedEntities: selectedEntities,
            };
        }
        case ActionType.RemoveSelectedEntity:
            return {
                ...state,
                selectedEntities: state.selectedEntities.filter((e) => !action.entities.includes(e as string)),
            };
        case ActionType.SetSelectedEntities: {
            let selectedEntities = action.entities;
            selectedEntities = selectedEntities.filter((e, i) => selectedEntities.indexOf(e) === i);
            return { ...state, selectedEntities: selectedEntities };
        }
        case ActionType.AddHighlightedEntities:
            return {
                ...state,
                highlightedEntities: [...state.highlightedEntities, ...action.entities],
            };
        case ActionType.RemoveHighlightedEntities:
            return {
                ...state,
                highlightedEntities: state.highlightedEntities.filter((e) => !action.entities.includes(e as string)),
            };
        case ActionType.SetHighlightedEntities:
            return { ...state, highlightedEntities: action.entities };
        case ActionType.AddInvisibleEntities:
            return {
                ...state,
                invisibleEntities: [...state.invisibleEntities, ...action.entities],
            };
        case ActionType.RemoveInvisibleEntities:
            return {
                ...state,
                invisibleEntities: [...state.invisibleEntities.filter((e) => !action.entities.includes(e as string))],
            };
        case ActionType.SetInvisibleEntities: {
            const invisibleEntities = [...action.entities];
            const invisibleEntitiesHash = new Set(invisibleEntities);
            return {
                ...state,
                invisibleEntities,
                invisibleEntitiesHash,
            };
        }
        case ActionType.AddXrayedEntities:
            return {
                ...state,
                xrayedEntities: [...state.xrayedEntities, ...action.entities],
            };
        case ActionType.RemoveXrayedEntities:
            return {
                ...state,
                xrayedEntities: state.xrayedEntities.filter((e) => !action.entities.includes(e as string)),
            };
        case ActionType.SetXrayedEntities:
            return { ...state, xrayedEntities: action.entities };
        case ActionType.ScheduleIsolatedMeasure:
            return {
                ...state,
                scheduleIsolatedMeasure: true,
                isolatedMeasureInvisibles: action.entities,
            };
        case ActionType.StopIsolatedMeasure:
            return {
                ...state,
                scheduleIsolatedMeasure: false,
                isolatedMeasureInvisibles: [],
            };
        case ActionType.SetPickerIsEnabled:
            return { ...state, pickerIsEnabled: action.enabled };
        case ActionType.SetOpenPlugin:
            return { ...state, openPlugin: action.plugin };
        case ActionType.SetSectionPlanePlugin:
            return { ...state, sectionPlanesPlugin: action.sectionPlane };
        case ActionType.SetSectionPlanes:
            return { ...state, sectionPlanes: action.sectionPlanes };
        case ActionType.SetProjection:
            return { ...state, projection: action.projection };
        case ActionType.SetTreeViewCache:
            return { ...state, treeViewCache: action.treeViewCacheItem };
        case ActionType.SetGroupViewCache:
            return { ...state, groupViewCache: action.groupViewCacheItem };
        case ActionType.SetGridVisibility:
            return { ...state, gridEnabled: action.visible };
        case ActionType.SetSpaceVisibility:
            return { ...state, spaceEnabled: action.visible };
        case ActionType.ToggleAddingSectionPlane:
            return { ...state, addingSectionPlane: action.active };
        case ActionType.ToggleInvisibleAsXray:
            return { ...state, invisibleAsXray: action.active };
        case ActionType.SetTreeviewObjects:
            return { ...state, treeviewObjects: action.treeviewObjects };
        case ActionType.SetGroupviewObjects:
            return { ...state, groupViewObjects: action.groupviewObjects };
        case ActionType.SetTreeviewRender:
            return { ...state, treeviewRender: action.treeviewRender };
        case ActionType.SetGroupviewRender:
            return { ...state, groupViewRender: action.groupviewRender };
        default:
            return state;
    }
};

export const addSelectedEntities = (entities: (string | number)[]): AddSelectedEntities => ({
    type: ActionType.AddSelectedEntities,
    entities: entities as string[],
});
export const removeSelectedEntity = (entities: (string | number)[]): RemoveSelectedEntity => ({
    type: ActionType.RemoveSelectedEntity,
    entities: entities as string[],
});

export const setSelectedEntities = (entities: (string | number)[]): SetSelectedEntities => ({
    type: ActionType.SetSelectedEntities,
    entities: entities as string[],
});

export const addHighlightedEntities = (entities: (string | number)[]): AddHighlightedEntities => ({
    type: ActionType.AddHighlightedEntities,
    entities: entities as string[],
});
export const removeHighlightedEntities = (entities: (string | number)[]): RemoveHighlightedEntities => ({
    type: ActionType.RemoveHighlightedEntities,
    entities: entities as string[],
});
export const setHighlightedEntities = (entities: (string | number)[]): SetHighlightedEntities => ({
    type: ActionType.SetHighlightedEntities,
    entities: entities as string[],
});

export const addInvisibleEntities = (entities: (string | number)[]): AddInvisibleEntities => ({
    type: ActionType.AddInvisibleEntities,
    entities: entities as string[],
});
export const removeInvisibleEntities = (entities: (string | number)[]): RemoveInvisibleEntities => ({
    type: ActionType.RemoveInvisibleEntities,
    entities: entities as string[],
});
export const setInvisibleEntities = (entities: (string | number)[]): SetInvisibleEntities => ({
    type: ActionType.SetInvisibleEntities,
    entities: entities as string[],
});

export const addXrayedEntities = (entities: (string | number)[]): AddXrayedEntities => ({
    type: ActionType.AddXrayedEntities,
    entities: entities as string[],
});

export const removeXrayedEntities = (entities: (string | number)[]): RemoveXrayedEntities => ({
    type: ActionType.RemoveXrayedEntities,
    entities: entities as string[],
});

export const setXrayedEntities = (entities: (string | number)[]): SetXrayedEntities => ({
    type: ActionType.SetXrayedEntities,
    entities: entities as string[],
});

export const scheduleIsolatedMeasure = (entities: (string | number)[]): ScheduleIsolatedMeasure => ({
    type: ActionType.ScheduleIsolatedMeasure,
    entities: entities as string[],
});
export const stopIsolatedMeasure = (): StopIsolatedMeasure => ({
    type: ActionType.StopIsolatedMeasure,
});

export const setPickerIsEnabled = (enabled: boolean): SetPickerIsEnabled => ({
    type: ActionType.SetPickerIsEnabled,
    enabled,
});

export const setOpenPlugin = (plugin: "" | "SectionPlanes" | "Measurer"): SetOpenPlugin => ({
    type: ActionType.SetOpenPlugin,
    plugin: plugin,
});

export const setSectionPlanePlugin = (plugin: SectionPlanesPlugin): SetSectionPlanePlugin => ({
    type: ActionType.SetSectionPlanePlugin,
    sectionPlane: plugin,
});
export const setSectionPlanes = (sectionPlanes: SectionPlane[]): SetSectionPlanes => ({
    type: ActionType.SetSectionPlanes,
    sectionPlanes,
});

export const setProjection = (projection: "perspective" | "ortho" | "frustum"): SetProjection => ({
    type: ActionType.SetProjection,
    projection,
});

export const setTreeViewCache = (treeViewCacheItem: TreeViewCacheItem[]): ViewerActions => ({
    type: ActionType.SetTreeViewCache,
    treeViewCacheItem,
});
export const setTreeviewObjects = (treeviewObjects: TreeviewModel[]): ViewerActions => ({
    type: ActionType.SetTreeviewObjects,
    treeviewObjects,
});

export const setGroupViewCache = (groupViewCacheItem: GroupviewCacheItem[]): ViewerActions => ({
    type: ActionType.SetGroupViewCache,
    groupViewCacheItem,
});

export const setGridVisibility = (visible: boolean): ViewerActions => ({
    type: ActionType.SetGridVisibility,
    visible,
});
export const setSpaceVisibility = (visible: boolean): ViewerActions => ({
    type: ActionType.SetSpaceVisibility,
    visible,
});

export const toggleAddingSectionPlane = (active: boolean): ViewerActions => ({
    type: ActionType.ToggleAddingSectionPlane,
    active,
});

export const ToggleInvisibleAsXray = (active: boolean): ViewerActions => ({
    type: ActionType.ToggleInvisibleAsXray,
    active,
});

export const setGroupviewObjects = (groupviewObjects: GroupviewModel[][]): ViewerActions => ({
    type: ActionType.SetGroupviewObjects,
    groupviewObjects,
});
export const setTreeviewRender = (treeviewRender: TreeRenderElement[]): ViewerActions => ({
    type: ActionType.SetTreeviewRender,
    treeviewRender,
});

export const setGroupviewRender = (groupviewRender: TreeRenderElement[]): ViewerActions => ({
    type: ActionType.SetGroupviewRender,
    groupviewRender,
});

export default viewerReducer;
